/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { headersToPlainObject, isLocal } from '@squiz/dxp-console-support';
import { Observable } from 'rxjs';
import { AjaxResponse, ajax } from 'rxjs/ajax';
import { map } from 'rxjs/operators';
import { JsonObject } from 'type-fest';
import { HttpMethod } from '../../../../Constants/Methods/Methods.constant';
import { HttpRequestOptions } from '../../../../Types/RequestOptions/RequestOptions.type';
import { HttpResponse } from '../../../../Types/Response/Response.type';
import { HttpBaseAdapter } from '../Http.adapter';
import { HttpAdapterContract } from '../Http.adapter.dto';
import { HttpRxAdapterNewDto } from './HttpRx.adapter.dto';

/**
 * Makes http requests using `rxjs/rx`.
 */
export class HttpRxAdapter extends HttpBaseAdapter implements HttpAdapterContract {
  /**
   * Creates an instance of HttpRxAdapter.
   */
  public constructor({}: HttpRxAdapterNewDto) {
    super({});
  }

  /**
   * Make a request.
   * @template T The http request body type.
   * @template R The http response type.
   */
  public request<T = JsonObject, R = unknown>(
    method: HttpMethod,
    url: string,
    options: HttpRequestOptions<T> = {},
  ): Observable<R> {
    const { body, headers: httpHeaders, timeout, withCredentials } = options;
    const headers = httpHeaders ? headersToPlainObject(httpHeaders as HeadersInit) : undefined;

    // Workaround for RxJS v7 to let API calls work from local environment.
    // If not set to true, the "x-requested-with: XMLHttpRequest" header is appended and breaks CORS.
    const crossDomain = isLocal();

    return ajax({
      body,
      crossDomain,
      headers,
      method,
      timeout,
      url,
      withCredentials,
    }).pipe(
      map(({ response, status }: AjaxResponse<unknown>) => {
        return {
          body: response,
          headers: undefined,
          status: status,
          statusText: undefined,
          url: undefined,
        } as HttpResponse as R;
      }),
    );
  }
}
